<template>
  <div class="px-4 py-2 border border-gray-300 rounded">
    <div class="flex justify-between">
      <div class="flex items-center space-x-4">
        <div>
          <input
            type="checkbox"
            v-model="shouldProcess"
            :id="`should-process-${_uid}`"
            :disabled="!canBeSelected"
            v-on:change="toggleProcessing()"
          />
        </div>
        <label :for="`should-process-${_uid}`">
          <div class="text-lg">{{ product.Product }}</div>
          <div class="text-gray-500">{{ product.SKU }}</div>
        </label>
      </div>
      <div class="flex flex-col items-end space-y-1 whitespace-no-wrap">
        <span
          v-if="hasShipped"
          class="px-2 bg-green-500 text-xs text-white rounded-full"
        >
          Shipped {{ this.dateFormat(product.DateShipped) }}
        </span>
        <span v-else class="px-2 text-xs"> Not Shipped </span>
        <div>
          <span v-if="isDropShip">Drop Ship</span>
          <span
            v-else
            class="px-2 text-xs text-white rounded-full"
            :class="{ 'bg-red-500': !stock, 'bg-green-500': stock }"
          >
            {{ stock }} in stock
          </span>
        </div>
      </div>
    </div>
    <div v-if="qualifiesForWarranty">
      <div
        v-if="canBeReshipped"
        class="w-full flex justify-end items-center space-x-4"
      >
        <span>Quantity:</span>
        <input
          v-model="originalSKUQuantity"
          v-on:input="updateProcessing()"
          class="w-24 px-1 border rounded"
          :class="{
            'border-green-500': shouldProcess && isOriginalSKUQuantityValid,
            'border-red-500': shouldProcess && !isOriginalSKUQuantityValid,
          }"
        />
      </div>
      <div v-else class="w-full flex justify-end items-center space-x-4">
        <span v-if="isNotShippable" class="text-right text-gray-500"
          >This product can no longer be reshipped.</span
        >
        <span v-else>Not in stock!</span>
        <span
          v-if="canBeReplaced"
          v-on:click="toggleReplacements"
          class="underline cursor-pointer"
        >
          <span v-text="showReplacements ? 'hide' : 'show'"></span>
          possible replacements
        </span>
      </div>

      <div v-if="showReplacements">
        <div v-if="loadingReplacements">loading ...</div>
        <div v-else>
          <form>
            <table class="w-full border-collapse border border-gray-300">
              <tr>
                <th class="border border-gray-300"></th>
                <th class="border border-gray-300">SKU</th>
                <th class="border border-gray-300">Qty</th>
                <th class="border border-gray-300">Reship Qty</th>
                <th class="border border-gray-300">Status</th>
              </tr>

              <tr
                v-for="(inventory, index) in replacements"
                :key="index"
                class="bg-white even:bg-gray-100"
              >
                <td class="px-2 text-center border border-gray-300">
                  <input
                    type="radio"
                    v-model="replacement"
                    :value="inventory.sku"
                    :id="`replacement_${_uid}_${index}`"
                    name="replacement"
                  />
                </td>
                <td class="px-2 border border-gray-300">
                  <label :for="`replacement_${_uid}_${index}`">
                    {{ inventory.sku }}
                  </label>
                </td>
                <td class="px-2 text-right border border-gray-300">
                  {{ inventory.quantity }}
                </td>
                <td class="text-center border border-gray-300">
                  <input
                    v-model="inventory.quantityToProcess"
                    v-on:input="updateProcessing()"
                    class="w-24 px-1 border rounded"
                    :class="{
                      'border-green-500':
                        shouldProcess &&
                        replacement == inventory.sku &&
                        isInventoryQuantityValid(inventory),
                      'border-red-500':
                        shouldProcess &&
                        replacement == inventory.sku &&
                        !isInventoryQuantityValid(inventory),
                    }"
                  />
                </td>
                <td class="text-center border border-gray-300">
                  Available Now!
                </td>
              </tr>

              <tr class="bg-white even:bg-gray-100">
                <td class="px-2 text-center border border-gray-300">
                  <input
                    type="radio"
                    v-model="replacement"
                    :value="product.SKU"
                    :id="`replacement_${_uid}_last`"
                    name="replacement"
                  />
                </td>
                <td class="px-2 border border-gray-300">
                  <label :for="`replacement_${_uid}_last`">{{
                    product.SKU
                  }}</label>
                </td>
                <td class="px-2 text-right border border-gray-300">
                  {{ stock }}
                </td>
                <td class="text-center border border-gray-300">
                  <input
                    v-model="originalReplacementQuantity"
                    v-on:input="updateProcessing()"
                    class="w-24 px-1 border rounded"
                    :class="{
                      'border-green-500':
                        shouldProcess &&
                        replacement == product.SKU &&
                        isOriginalReplacementQuantityValid,
                      'border-red-500':
                        shouldProcess &&
                        replacement == product.SKU &&
                        !isOriginalReplacementQuantityValid,
                    }"
                  />
                </td>
                <td class="text-center font-bold border border-gray-300">
                  <label :for="`replacement_${_uid}_last`">
                    Original SKU:<br />Select for gift<br />card creation!
                  </label>
                </td>
              </tr>
            </table>
          </form>
          <div v-if="replacement">
            <button
              v-on:click="replacement = null"
              class="mt-2 py-1 px-2 bg-gray-100 hover:bg-gray-300 border border-gray-300 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-right text-gray-500">
      This product does not qualify for warranty!
    </div>
  </div>
</template>

<script>
import { ApiFactory } from "@/api/ApiFactory.js";

const Inventory = ApiFactory.get("inventories");

export default {
  name: "WarrantyClaimProduct",
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      replacement: null,
      replacements: [],
      originalReplacementQuantity: null,
      showReplacements: false,
      loadingReplacements: false,
      loadedReplacements: false,
      shouldProcess: false,
      originalSKUQuantity: null,
      warrantyStart: process.env.VUE_APP_GUARANTEE_WINDOW || 37, //GC end after 97 days, See ET-1007 for details
      warrantyEnd: 372, //Warranty Ends after 372 days, See ET-240 for details
    };
  },
  async beforeMount() {},
  computed: {
    hasShipped() {
      return Boolean(this.product?.DateShipped);
    },
    isNotShippable() {
      return Number(this.product?.inventory?.NotShippable);
    },
    isDropShip() {
      return Number(this.product?.inventory?.DropShip);
    },
    stock() {
      return Number(this.product?.inventory?.inventory_counts?.true_qoh);
    },
    qualifiesForWarranty() {
      if (!this.product.DateShipped) {
        return false;
      }

      if (
        this.product?.warrantyCounts?.warranty_count &&
        Number(this.product?.warrantyCounts?.warranty_count) >=
          Number(this.product.QuantityShipped)
      ) {
        return false;
      }

      let dateDiff = this.diffInDays(
        new Date(),
        new Date(this.product.DateShipped)
      );
      if (dateDiff > this.warrantyEnd || dateDiff <= this.warrantyStart) {
        return false;
      }

      return true;
    },
    canBeSelected() {
      if (!this.qualifiesForWarranty) {
        return false;
      }

      if (this.isDropShip) {
        return true;
      }

      if (!this.stock) {
        return false;
      }

      return true;
    },
    canBeReplaced() {
      return !this.stock && !this.isDropShip;
    },
    canBeReshipped() {
      if (this.isNotShippable) {
        return false;
      }
      return this.stock || this.isDropShip;
    },
    orderedQuantity() {
      return Number(this.product.QuantityOrdered);
    },
    isOriginalSKUQuantityValid() {
      if (
        Number(this.originalSKUQuantity) >
        this.orderedQuantity -
          Number(this.product?.warrantyCounts?.warranty_count)
      ) {
        return false;
      }
      return (
        Number(this.originalSKUQuantity) > 0 &&
        Number(this.originalSKUQuantity) <= this.orderedQuantity
      );
    },
    isOriginalReplacementQuantityValid() {
      if (
        Number(this.originalReplacementQuantity) >
        this.orderedQuantity -
          Number(this.product?.warrantyCounts?.warranty_count)
      ) {
        return false;
      }
      return (
        Number(this.originalReplacementQuantity) > 0 &&
        Number(this.originalReplacementQuantity) <= this.orderedQuantity
      );
    },
    quantityToProcess() {
      if (!this.shouldProcess) {
        return 0;
      }

      if (this.isNotShippable) {
        return this.orderedQuantity;
      }

      if (!this.replacement) {
        return Number(this.originalSKUQuantity);
      }

      if (this.replacement == this.product.SKU) {
        return Number(this.originalReplacementQuantity);
      }

      let element = this.replacements.find(
        (item) => item.sku == this.replacement
      );
      if (!element) {
        return 0;
      }

      return Number(element.quantityToProcess);
    },
  },
  watch: {
    replacement(value) {
      this.shouldProcess = Boolean(value);
      this.toggleProcessing();
    },
  },
  methods: {
    toggleReplacements() {
      this.showReplacements = !this.showReplacements;

      if (this.showReplacements && !this.loadedReplacements) {
        this.loadReplacements();
      }
    },
    loadReplacements() {
      this.loadingReplacements = true;

      Inventory.replacements(this.product.SKU)
        .then((response) => {
          this.loadedReplacements = true;
          this.replacements = response.data.body.map((item) => {
            item.quantityToProcess = null;
            return item;
          });
        })
        .finally(() => {
          this.loadingReplacements = false;
        });
    },
    dateFormat(dateString, options) {
      let date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        ...{ year: "numeric", month: "long", day: "numeric" },
        ...options,
      });
    },
    toggleProcessing() {
      let event = this.shouldProcess
        ? "add-to-processing"
        : "remove-from-processing";

      this.$emit(event, this.processingValues());
    },
    updateProcessing() {
      if (!this.shouldProcess) {
        return;
      }
      this.$emit("update-processing", this.processingValues());
    },
    processingValues() {
      return {
        sku: this.product.SKU,
        orderedQuantity: this.orderedQuantity,
        replacement: this.replacement,
        itemNumber: this.product.ItemNumber,
        quantity: this.quantityToProcess,
        isNotShippable: this.isNotShippable,
        warrantyCount: Number(this.product?.warrantyCounts?.warranty_count),
      };
    },
    diffInDays(minuend, subtrahend) {
      return Math.ceil(
        (minuend.getTime() - subtrahend.getTime()) / (24 * 60 * 60 * 1000)
      );
    },
    isInventoryQuantityValid(inventory) {
      if (Number(inventory.quantityToProcess) <= 0) {
        return false;
      }

      if (Number(inventory.quantityToProcess) > this.orderedQuantity) {
        return false;
      }

      return true;
    },
  },
};
</script>
